/**
 * ComposeEmailDialog — Reusable email composition dialog.
 *
 * Supports:
 * - Internal org users (with presence indicators) + external emails
 * - Content treatment: raw, summarized, formalized
 * - File attachments + internal document attachments
 * - Entity linking (for contextual sharing from entity detail panels)
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Send,
  Paperclip,
  Loader2,
  Mail,
  User as UserIcon,
  Contact as ContactIcon,
  ChevronDown,
  ChevronUp,
  Building2,
  FolderKanban,
  Search,
  Check,
  FileText,
  Link2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { RichTextEditor } from "@/components/RichTextEditor";
import { emailCompositionApi, contactsApi, documentsApi } from "@/api/services";
import { useProjects } from "@/api/hooks";
import { useAuthStore } from "@/stores/auth";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import type {
  EmailRecipient,
  UserSearchResult,
  Contact,
  Document,
  EmailInlineAttachment,
  ComposeEmailRequest,
} from "@/api/types";

// Client-side ceilings mirror the server (app/models/email.py) so the user
// gets immediate feedback instead of a rejected send.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES_TOTAL_BYTES = 20 * 1024 * 1024;

/** Read a File into a base64 string (no data: URI prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ─── Unified picker item ──────────────────────────────────────────
// The recipient picker draws from two real sources: org users (internal
// teammates, with presence) AND the org contacts table (#586 — people you've
// emailed / met, incl. external addresses). Both are normalized to this one
// shape so the dropdown renders + selects them identically.
type PickerItem = {
  key: string;
  email: string;
  display_name: string;
  source: "user" | "contact";
  /** Secondary line (company for contacts). */
  subtitle?: string;
  /** Users only. */
  presence_status?: string;
  /** Users only — links the recipient back to the internal user. */
  user_id?: string;
};

function userToItem(u: UserSearchResult): PickerItem {
  return {
    key: `user:${u.id}`,
    email: u.email,
    display_name: u.display_name,
    source: "user",
    presence_status: u.presence_status,
    user_id: u.id,
  };
}

function contactToItem(c: Contact): PickerItem {
  return {
    key: `contact:${c.id}`,
    email: c.email ?? "",
    display_name: c.display_name || c.email || "",
    source: "contact",
    subtitle: c.company || c.title || undefined,
  };
}

/** Merge users + contacts, dedup by lowercased email (prefer the user record
 *  since it carries presence + a user_id), cap the list. */
function mergeRecipients(
  users: PickerItem[],
  contacts: PickerItem[],
  limit = 10,
): PickerItem[] {
  const byEmail = new Map<string, PickerItem>();
  for (const u of users) {
    if (u.email) byEmail.set(u.email.toLowerCase(), u);
  }
  for (const c of contacts) {
    const k = c.email.toLowerCase();
    if (k && !byEmail.has(k)) byEmail.set(k, c);
  }
  return Array.from(byEmail.values()).slice(0, limit);
}

// ─── Presence dot colors ──────────────────────────────────────────
const presenceColors: Record<string, string> = {
  online: "bg-green-500",
  away: "bg-yellow-500",
  offline: "bg-gray-400",
};

// ─── Props ────────────────────────────────────────────────────────
interface ComposeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  prefillSubject?: string;
  prefillBody?: string;
  prefillRecipients?: EmailRecipient[];
  entityType?: string;
  entityId?: string;
  /** Initial "About" scope — the project this email is about (null/undefined =
   *  organization-level). The user can change it in the dialog. */
  projectId?: string;
  /** Display name for the initial project scope (for the chip). */
  projectName?: string;
}

// ─── Recipient Chip ───────────────────────────────────────────────
function RecipientChip({
  recipient,
  onRemove,
}: {
  recipient: EmailRecipient;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 dark:bg-brand-900/30 px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-300">
      {recipient.display_name || recipient.email}
      <button
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-brand-200 dark:hover:bg-brand-800 transition-colors"
        aria-label={`Remove ${recipient.display_name || recipient.email}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Recipient Picker ─────────────────────────────────────────────
function RecipientPicker({
  label,
  recipients,
  onAdd,
  onRemove,
  flushRef,
}: {
  label: string;
  recipients: EmailRecipient[];
  onAdd: (r: EmailRecipient) => void;
  onRemove: (index: number) => void;
  /** Parent registers a flush here so clicking Send commits a typed-but-
   *  uncommitted email still sitting in the input (the "Send won't execute"
   *  trap — Bob 2026-06-08). Returns the recipient it committed, or null. */
  flushRef?: React.MutableRefObject<(() => EmailRecipient | null) | null>;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PickerItem[]>([]);
  const [recent, setRecent] = useState<PickerItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The onBlur "commit typed email" timer (150ms). Held in a ref + cleared on
  // unmount so it can't fire after the component is gone — a stray fire calls
  // setState on an unmounted tree (React "update on unmounted component", and
  // in jsdom teardown a `window is not defined` crash that flaked CI).
  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  // Search BOTH org users (presence) and the contacts table (#586) in
  // parallel, then merge+dedup so the picker is "hooked to contacts" — not
  // just internal teammates (Bob 2026-06-08).
  const searchRecipients = useCallback(async (q: string) => {
    if (q.length < 1) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const [userData, contactData] = await Promise.all([
        emailCompositionApi.searchUsers(q, 10).catch(() => ({ users: [] })),
        contactsApi
          .list({ search: q, limit: 8 })
          .catch(() => ({ items: [] })) as Promise<{ items: Contact[] }>,
      ]);
      const merged = mergeRecipients(
        (userData.users || []).map(userToItem),
        (contactData.items || []).map(contactToItem),
      );
      setResults(merged);
      setShowDropdown(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Outlook-style: on focus with an empty box, suggest the people you've most
  // recently been in touch with (the contacts table's recent list).
  const loadRecent = useCallback(async () => {
    if (recent.length > 0) return;
    try {
      const data = (await contactsApi.recent(8)) as { items: Contact[] };
      setRecent((data.items || []).map(contactToItem));
    } catch {
      /* recent is a nice-to-have; ignore failures */
    }
  }, [recent.length]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length >= 1) {
      debounceRef.current = setTimeout(() => searchRecipients(query), 300);
    } else {
      setResults([]);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searchRecipients]);

  // What the dropdown shows: live search results when typing, else the recent
  // suggestions when the box is focused-but-empty.
  const items: PickerItem[] = query.length >= 1 ? results : recent;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Commit a valid email currently typed in the box into a recipient chip.
  // Returns the committed recipient (so a synchronous flush can use it even
  // before React re-renders), or null when there's nothing valid to commit.
  const commitQuery = useCallback((): EmailRecipient | null => {
    const q = query.trim();
    if (!q || !EMAIL_RE.test(q)) return null;
    const recipient: EmailRecipient = {
      email: q.toLowerCase(),
      type: label.toLowerCase() as "to" | "cc" | "bcc",
    };
    onAdd(recipient);
    setQuery("");
    setShowDropdown(false);
    return recipient;
  }, [query, label, onAdd]);

  // Expose the flush to the parent so clicking Send commits a pending email.
  useEffect(() => {
    if (flushRef) flushRef.current = commitQuery;
    return () => {
      if (flushRef) flushRef.current = null;
    };
  }, [flushRef, commitQuery]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter / comma / Tab all commit a typed email (Gmail-style).
    if ((e.key === "Enter" || e.key === "," || e.key === "Tab") && query.trim()) {
      if (EMAIL_RE.test(query.trim())) {
        e.preventDefault();
        commitQuery();
      }
    }
  };

  const selectItem = (item: PickerItem) => {
    onAdd({
      email: item.email,
      user_id: item.user_id,
      type: label.toLowerCase() as "to" | "cc" | "bcc",
      display_name: item.display_name,
    });
    setQuery("");
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {label}
      </label>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all">
        {recipients.map((r, i) => (
          <RecipientChip key={`${r.email}-${i}`} recipient={r} onRemove={() => onRemove(i)} />
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Commit a typed email when leaving the field so it becomes a chip
            // (and clicking Send doesn't silently drop it). Delay slightly so a
            // dropdown-option click registers as a selection first. Held in a
            // ref so unmount can cancel it (no setState after teardown).
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = setTimeout(() => commitQuery(), 150);
          }}
          onFocus={() => {
            void loadRecent();
            setShowDropdown(true);
          }}
          placeholder={
            recipients.length === 0
              ? "Type name to search or enter email..."
              : ""
          }
          className="flex-1 min-w-[150px] bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-500 dark:text-slate-400"
        />
        {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500 dark:text-slate-400" />}
      </div>

      {/* Search results / recent-contacts dropdown */}
      {showDropdown && items.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
          data-testid="recipient-dropdown"
        >
          {query.length < 1 && (
            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Recent contacts
            </p>
          )}
          {items.map((user) => (
            <button
              key={user.key}
              onClick={() => selectItem(user)}
              data-testid={`recipient-option-${user.source}`}
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40"
            >
              <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30 text-xs font-semibold text-brand-700 dark:text-brand-300">
                {user.source === "contact" ? (
                  <ContactIcon className="h-3.5 w-3.5" />
                ) : (
                  <UserIcon className="h-3.5 w-3.5" />
                )}
                {user.source === "user" && (
                  <span
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface",
                      presenceColors[user.presence_status || ""] || "bg-gray-400",
                    )}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900 dark:text-slate-100">
                  {user.display_name}
                  {user.subtitle && (
                    <span className="ml-1.5 font-normal text-slate-400 dark:text-slate-500">
                      · {user.subtitle}
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {user.email}
                </p>
              </div>
              {user.source === "user" ? (
                <span
                  className={cn(
                    "text-[10px] capitalize",
                    user.presence_status === "online"
                      ? "text-green-600"
                      : user.presence_status === "away"
                        ? "text-yellow-600"
                        : "text-slate-500 dark:text-slate-400",
                  )}
                >
                  {user.presence_status}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Contact
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FTL document link picker ─────────────────────────────────────
// Lets the sender attach documents from FTL's own repositories (Bob
// 2026-06-08: "integrated link options for the docs that are in our own
// repositories"). Selected docs flow to `attachment_document_ids` on the
// compose request, which the backend renders + attaches to the email.
function DocLinkPicker({
  selected,
  onChange,
}: {
  selected: { id: string; title: string }[];
  onChange: (docs: { id: string; title: string }[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["compose-doc-picker"],
    queryFn: () => documentsApi.list({ limit: 100 }),
    enabled: open,
    staleTime: 60_000,
  });
  const docs: Document[] = (data?.items as Document[]) ?? [];
  const selectedIds = new Set(selected.map((d) => d.id));
  const filtered = docs.filter(
    (d) =>
      !selectedIds.has(d.id) &&
      (query.trim()
        ? (d.title || "").toLowerCase().includes(query.trim().toLowerCase())
        : true),
  );

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="compose-doclink-toggle"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
        >
          <Link2 className="h-3.5 w-3.5" /> Attach FTL document
        </button>
        {selected.map((d) => (
          <span
            key={d.id}
            data-testid="compose-doclink-chip"
            className="inline-flex items-center gap-1 rounded-md bg-brand-50 dark:bg-brand-900/30 px-2 py-1 text-xs text-brand-700 dark:text-brand-300"
          >
            <FileText className="h-3 w-3" />
            <span className="max-w-[160px] truncate">{d.title || "Untitled"}</span>
            <button
              type="button"
              aria-label={`Remove ${d.title || "document"}`}
              onClick={() => onChange(selected.filter((x) => x.id !== d.id))}
              className="rounded p-0.5 hover:bg-brand-100 dark:hover:bg-brand-800/40"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      {open && (
        <div
          className="absolute left-0 z-50 mt-1 w-80 max-w-[90vw] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
          data-testid="compose-doclink-dropdown"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-3 py-2">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FTL documents…"
              className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">
                {docs.length === 0 ? "No documents found." : "No matching documents."}
              </p>
            ) : (
              filtered.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  data-testid="compose-doclink-option"
                  onClick={() => {
                    onChange([...selected, { id: d.id, title: d.title || "Untitled" }]);
                    setQuery("");
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <FileText className="h-4 w-4 shrink-0 text-brand-500" />
                  <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
                    {d.title || "Untitled"}
                  </span>
                  {d.doc_type && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                      {d.doc_type.replace(/_/g, " ")}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Dialog ──────────────────────────────────────────────────
export function ComposeEmailDialog({
  open,
  onClose,
  prefillSubject = "",
  prefillBody = "",
  prefillRecipients = [],
  entityType,
  entityId,
  projectId,
  projectName,
}: ComposeEmailDialogProps) {
  // Sender identity — so the composer shows who the email is being sent AS
  // (Bob 2026-06-08: "it did not know of my email address"). Read from the
  // authenticated user; the backend sends from this account.
  const senderEmail = useAuthStore((s) => s.user?.email);

  // Linked FTL documents — attached to the email as their rendered content via
  // the existing `attachment_document_ids` compose field (consumed in
  // email_composition_service). Bob 2026-06-08: "integrated link options for
  // the docs that are in our own repositories." We keep {id,title} so we can
  // render removable chips without re-fetching.
  const [linkedDocs, setLinkedDocs] = useState<{ id: string; title: string }[]>([]);
  // Uploaded file attachments (Bob 2026-06-08: "no ability to add an
  // attachment"). Carried inline as base64 → backend `attachments` field.
  const [files, setFiles] = useState<(EmailInlineAttachment & { size: number })[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // "About" scope — which project this email concerns (null = org-level).
  // Seeded from the caller's current scope; user can change it in the dialog.
  const [scopeProjectId, setScopeProjectId] = useState<string | null>(projectId ?? null);
  const [scopeProjectName, setScopeProjectName] = useState<string | null>(projectName ?? null);
  // Template-layer SKIN for the "attach documents" section label — follows the
  // scoped project's manifest vocabulary (org-level scope = null = "Documents").
  const { label: skinLabel } = useTemplateLabels(scopeProjectId);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeQuery, setScopeQuery] = useState("");
  const { data: projectsData } = useProjects();
  const projectList = (
    ((projectsData as { items?: { id: string; name: string }[] } | undefined)?.items ??
      (projectsData as unknown as { id: string; name: string }[] | undefined) ??
      []) as { id: string; name: string }[]
  ).filter((p) =>
    scopeQuery.trim() ? p.name?.toLowerCase().includes(scopeQuery.trim().toLowerCase()) : true,
  );
  const [toRecipients, setToRecipients] = useState<EmailRecipient[]>(
    prefillRecipients.filter((r) => r.type === "to" || !r.type),
  );
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>(
    prefillRecipients.filter((r) => r.type === "cc"),
  );
  const [bccRecipients, setBccRecipients] = useState<EmailRecipient[]>(
    prefillRecipients.filter((r) => r.type === "bcc"),
  );
  const [showCcBcc, setShowCcBcc] = useState(false);
  // Flush handles so Send commits a typed-but-uncommitted email in each picker.
  const toFlush = useRef<(() => EmailRecipient | null) | null>(null);
  const ccFlush = useRef<(() => EmailRecipient | null) | null>(null);
  const bccFlush = useRef<(() => EmailRecipient | null) | null>(null);
  const [subject, setSubject] = useState(prefillSubject);
  const [body, setBody] = useState(prefillBody);
  // Rich-text HTML rendering of the body (from RichTextEditor) → sent as
  // body_html; `body` stays the plain-text fallback. bodyResetSignal reseeds
  // the editor on dialog open.
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyResetSignal, setBodyResetSignal] = useState(0);
  const [contentTreatment, setContentTreatment] = useState<
    "raw" | "summarized" | "formalized"
  >("raw");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // The "sent!" confirmation timer, held so it can be cancelled.
  //
  // A send scheduled `setTimeout(() => { onClose(); setSuccess(false) }, 1500)`
  // with nothing holding the id, so the callback outlived the component. In the
  // test environment that surfaced as an UNHANDLED ERROR — vitest reported
  // "3131 passed" and EXITED 1, with `ReferenceError: window is not defined`
  // thrown from React's scheduler when the timer fired after jsdom had been torn
  // down. vitest's own warning on that is "this might cause false positive
  // tests": an unhandled error during a run can mask a genuine failure, and a
  // suite whose summary line and exit code disagree is one where whichever you
  // happen to read decides what you believe.
  //
  // In the browser it is quieter but not harmless: closing the dialog inside
  // that 1.5s window leaves a callback that calls setState on an unmounted tree.
  //
  // Two sibling timers in this file (debounceRef, blurTimerRef) already use
  // exactly this pattern — this one was simply missed.
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  // Escape + focus-trap + focus-restore + scroll-lock for the compose dialog.
  const dialogRef = useModalA11y<HTMLDivElement>({ open, onClose });

  // Reset when opened with new prefills
  // Seed the form ONLY on the closed→open transition. Previously this ran on
  // every render where any prefill dep changed identity — and `prefillRecipients`
  // defaults to a fresh `[]` literal each render, so the effect re-fired on every
  // keystroke and instantly wiped `subject`/`body` ("typing does nothing"). The
  // `wasOpen` ref pins the reset to the actual open event, so user edits while
  // the dialog is open are never clobbered.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setToRecipients(
        prefillRecipients.filter((r) => r.type === "to" || !r.type),
      );
      setCcRecipients(prefillRecipients.filter((r) => r.type === "cc"));
      setBccRecipients(prefillRecipients.filter((r) => r.type === "bcc"));
      setSubject(prefillSubject);
      setBody(prefillBody);
      setBodyHtml("");
      setBodyResetSignal((n) => n + 1);
      setContentTreatment("raw");
      setScopeProjectId(projectId ?? null);
      setScopeProjectName(projectName ?? null);
      setLinkedDocs([]);
      setFiles([]);
      setFileError(null);
      setError(null);
      setSuccess(false);
    }
    wasOpen.current = open;
  }, [open, prefillSubject, prefillBody, prefillRecipients, projectId, projectName]);

  const handleAddFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      setFileError(null);
      const incoming = Array.from(fileList);
      const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
      if (tooBig) {
        setFileError(`"${tooBig.name}" exceeds the 10MB per-file limit.`);
        return;
      }
      try {
        const encoded = await Promise.all(
          incoming.map(async (f) => ({
            filename: f.name,
            content_base64: await fileToBase64(f),
            mime_type: f.type || undefined,
            size: f.size,
          })),
        );
        setFiles((prev) => {
          const next = [...prev, ...encoded];
          const total = next.reduce((sum, f) => sum + f.size, 0);
          if (total > MAX_FILES_TOTAL_BYTES) {
            setFileError("Attachments exceed the 20MB total limit.");
            return prev;
          }
          return next;
        });
      } catch {
        setFileError("Could not read one of the selected files.");
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [],
  );

  const handleSend = async () => {
    // Flush any typed-but-uncommitted email still in each picker's input so a
    // user who typed an address and clicked Send (without pressing Enter /
    // selecting) actually sends to it — the "Send won't execute" trap.
    const pendingTo = toFlush.current?.() ?? null;
    const pendingCc = ccFlush.current?.() ?? null;
    const pendingBcc = bccFlush.current?.() ?? null;
    const effectiveTo = pendingTo ? [...toRecipients, pendingTo] : toRecipients;
    const effectiveCc = pendingCc ? [...ccRecipients, pendingCc] : ccRecipients;
    const effectiveBcc = pendingBcc ? [...bccRecipients, pendingBcc] : bccRecipients;

    if (effectiveTo.length === 0) {
      setError("At least one To recipient is required");
      return;
    }
    if (!subject.trim()) {
      setError("Subject is required");
      return;
    }
    if (!body.trim()) {
      setError("Email body is required");
      return;
    }

    setSending(true);
    setError(null);

    try {
      const allRecipients = [
        ...effectiveTo.map((r) => ({ ...r, type: "to" as const })),
        ...effectiveCc.map((r) => ({ ...r, type: "cc" as const })),
        ...effectiveBcc.map((r) => ({ ...r, type: "bcc" as const })),
      ];

      // Only send body_html when the user actually applied formatting (the
      // editor emits a <tag> beyond bare text); otherwise plain text suffices.
      const hasRichFormatting = /<(b|strong|i|em|u|ul|ol|li|a|br|div|p)\b/i.test(bodyHtml);

      const request: ComposeEmailRequest = {
        subject: subject.trim(),
        body: body.trim(),
        body_html: hasRichFormatting ? bodyHtml : undefined,
        recipients: allRecipients,
        content_treatment: contentTreatment,
        entity_type: entityType,
        entity_id: entityId,
        project_id: scopeProjectId ?? undefined,
        attachment_document_ids: linkedDocs.length
          ? linkedDocs.map((d) => d.id)
          : undefined,
        attachments: files.length
          ? files.map(({ filename, content_base64, mime_type }) => ({
              filename,
              content_base64,
              mime_type,
            }))
          : undefined,
      };

      await emailCompositionApi.compose(request);
      setSuccess(true);
      successTimerRef.current = setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to send email";
      setError(message);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        {...dialogA11yProps({ labelledById: "compose-email-title" })}
        className="relative z-10 w-full max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl animate-fade-in mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-900/30">
              <Mail className="h-5 w-5 text-brand-600" />
            </div>
            <h2 id="compose-email-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Compose Email
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close compose email"
            className="rounded-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
          {/* From — sender identity (who this is sent as) */}
          {senderEmail && (
            <div
              className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"
              data-testid="compose-from"
            >
              <span className="font-medium uppercase tracking-wider">From</span>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200">
                <Mail className="h-3 w-3" />
                {senderEmail}
              </span>
            </div>
          )}

          {/* To recipients */}
          <RecipientPicker
            label="To"
            recipients={toRecipients}
            flushRef={toFlush}
            onAdd={(r) => setToRecipients((prev) => [...prev, r])}
            onRemove={(i) =>
              setToRecipients((prev) => prev.filter((_, idx) => idx !== i))
            }
          />

          {/* CC/BCC toggle */}
          <button
            onClick={() => setShowCcBcc(!showCcBcc)}
            className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 transition-colors"
          >
            {showCcBcc ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {showCcBcc ? "Hide" : "Show"} CC / BCC
          </button>

          {showCcBcc && (
            <>
              <RecipientPicker
                label="CC"
                recipients={ccRecipients}
                flushRef={ccFlush}
                onAdd={(r) => setCcRecipients((prev) => [...prev, r])}
                onRemove={(i) =>
                  setCcRecipients((prev) =>
                    prev.filter((_, idx) => idx !== i),
                  )
                }
              />
              <RecipientPicker
                label="BCC"
                recipients={bccRecipients}
                flushRef={bccFlush}
                onAdd={(r) => setBccRecipients((prev) => [...prev, r])}
                onRemove={(i) =>
                  setBccRecipients((prev) =>
                    prev.filter((_, idx) => idx !== i),
                  )
                }
              />
            </>
          )}

          {/* About: Org / Project scope — "what is this email about" */}
          <div className="relative">
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              About
            </label>
            <button
              type="button"
              onClick={() => setScopeOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={scopeOpen}
              className="mt-1 flex w-full items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-left text-sm text-slate-900 dark:text-slate-100 transition-colors hover:border-brand-500"
            >
              {scopeProjectId ? (
                <FolderKanban className="h-4 w-4 text-brand-500" />
              ) : (
                <Building2 className="h-4 w-4 text-brand-500" />
              )}
              <span className="flex-1 truncate">
                {scopeProjectId ? scopeProjectName || "Project" : "Organization (org-level)"}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
            {scopeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setScopeOpen(false)} />
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setScopeProjectId(null);
                      setScopeProjectName(null);
                      setScopeOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <Building2 className="h-4 w-4 text-brand-500" />
                    <span className="flex-1 text-slate-900 dark:text-slate-100">
                      Organization (org-level)
                    </span>
                    {!scopeProjectId && <Check className="h-4 w-4 text-brand-500" />}
                  </button>
                  <div className="border-t border-slate-200 dark:border-slate-700 px-2 py-1.5">
                    <div className="flex items-center gap-2 rounded-md bg-slate-50 dark:bg-slate-800/40 px-2 py-1.5">
                      <Search className="h-3.5 w-3.5 text-slate-400" />
                      <input
                        value={scopeQuery}
                        onChange={(e) => setScopeQuery(e.target.value)}
                        placeholder="Find a project…"
                        className="w-full bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto pb-1">
                    {projectList.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">No projects found.</div>
                    ) : (
                      projectList.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setScopeProjectId(p.id);
                            setScopeProjectName(p.name);
                            setScopeOpen(false);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        >
                          <FolderKanban className="h-4 w-4 text-slate-400" />
                          <span className="flex-1 truncate text-slate-900 dark:text-slate-100">
                            {p.name}
                          </span>
                          {scopeProjectId === p.id && <Check className="h-4 w-4 text-brand-500" />}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all placeholder:text-slate-500 dark:text-slate-400"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Body
            </label>
            <div className="mt-1">
              <RichTextEditor
                initialHtml={body}
                resetSignal={bodyResetSignal}
                placeholder="Write your email..."
                onChange={(html, plain) => {
                  setBodyHtml(html);
                  setBody(plain);
                }}
              />
            </div>
          </div>

          {/* Attach FTL documents */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {skinLabel("document", { plural: true, fallback: "Documents" })}
            </label>
            <div className="mt-1.5">
              <DocLinkPicker selected={linkedDocs} onChange={setLinkedDocs} />
            </div>
          </div>

          {/* Attach files */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Attachments
            </label>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                data-testid="compose-file-input"
                onChange={(e) => handleAddFiles(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="compose-attach-file"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
              >
                <Paperclip className="h-3.5 w-3.5" /> Attach a file
              </button>
              {files.map((f, i) => (
                <span
                  key={`${f.filename}-${i}`}
                  data-testid="compose-file-chip"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs text-slate-700 dark:text-slate-200"
                >
                  <Paperclip className="h-3 w-3" />
                  <span className="max-w-[160px] truncate">{f.filename}</span>
                  <span className="text-slate-400">
                    ({Math.max(1, Math.round(f.size / 1024))}KB)
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${f.filename}`}
                    onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    className="rounded p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            {fileError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="compose-file-error">
                {fileError}
              </p>
            )}
          </div>

          {/* Content treatment */}
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Content Treatment
            </label>
            <div className="mt-1.5 flex gap-2">
              {(
                [
                  { value: "raw", label: "Send as-is" },
                  { value: "summarized", label: "Summarize" },
                  { value: "formalized", label: "Formalize" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setContentTreatment(opt.value)}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-xs font-medium transition-all",
                    contentTreatment === opt.value
                      ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300"
                      : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {contentTreatment !== "raw" && (
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {contentTreatment === "summarized"
                  ? "AI will create a concise summary of your content."
                  : "AI will rewrite your content in a professional tone."}
              </p>
            )}
          </div>

          {/* Entity link indicator */}
          {entityType && (
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
              <Paperclip className="h-3.5 w-3.5" />
              Linked to {entityType.replace(/_/g, " ")}
              {entityId && ` (${entityId.slice(0, 8)}...)`}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2 text-sm text-green-700 dark:text-green-400">
              Email sent successfully!
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {toRecipients.length + ccRecipients.length + bccRecipients.length}{" "}
              recipient{toRecipients.length + ccRecipients.length + bccRecipients.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              // Always actionable (unless a send is in flight): handleSend
              // flushes a typed-but-uncommitted recipient and reports the exact
              // missing field, instead of a silently-disabled button that
              // "does nothing" when an address was typed but not committed.
              disabled={sending || success}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-all",
                sending || success
                  ? "bg-brand-400 cursor-not-allowed"
                  : "bg-brand-600 hover:bg-brand-700 active:scale-[0.98]",
              )}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? "Sending..." : success ? "Sent!" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
